<script lang="ts">
	// @ts-nocheck
	import TripadvisorAward from '$lib/components/TripadvisorAward.svelte';
	import { onMount } from 'svelte';
	import { Button } from '$lib/components/ui/button';
	import * as Carousel from '$lib/components/ui/carousel';
	import * as Card from '$lib/components/ui/card';
	import * as Tabs from '$lib/components/ui/tabs';
	import * as Accordion from '$lib/components/ui/accordion';
	import * as Alert from '$lib/components/ui/alert';
	import * as Select from '$lib/components/ui/select';
	import { Input } from '$lib/components/ui/input';
	import { Label } from '$lib/components/ui/label';
	import { Textarea } from '$lib/components/ui/textarea';
	import {
		Check,
		CheckCircle,
		Loader2,
		Star,
		MapPin,
		Leaf,
		Utensils,
		Plane,
		ArrowRight
	} from 'lucide-svelte';
	import Seo from '$lib/components/SEO.svelte';
	import allTours from '$lib/data/allTours.json';
	import TourCard from '$lib/components/tours/TourCard.svelte';

	// Hero
	import heroImage from '$lib/assets/enhanced/roaming-tasmania.jpg?enhanced';

	// Used in social proof section
	import cradleImage from '$lib/assets/enhanced/cradle-mountain.jpg?enhanced';

	let animate = $state(false);

	// Form state
	let submitted = $state(false);
	let loading = $state(false);
	let firstName = $state('');
	let lastName = $state('');
	let email = $state('');
	let university = $state('');
	let groupSize = $state('');
	let message = $state('');

	const groupSizeOptions = [
		{ value: '2-3', label: '2–3 people' },
		{ value: '4-5', label: '4–5 people' },
		{ value: '6-10', label: '6–10 people' },
		{ value: '11-20', label: '11–20 people' },
		{ value: '20+', label: '20+ people' }
	];

	const groupSizeTrigger = $derived(
		groupSizeOptions.find((o) => o.value === groupSize)?.label ?? 'Select group size'
	);

	function resetForm() {
		firstName = '';
		lastName = '';
		email = '';
		university = '';
		groupSize = '';
		message = '';
	}

	async function handleSubmit(e: Event) {
		e.preventDefault();
		loading = true;
		const formData = {
			name: `${firstName} ${lastName}`,
			email,
			university,
			groupSize,
			message,
			type: 'Student Group Enquiry'
		};
		try {
			const response = await fetch('/email/send-email', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(formData)
			});
			if (!response.ok) throw new Error('Failed to send email');
			const result = await response.json();
			if (result.success) {
				resetForm();
				submitted = true;
			} else {
				throw new Error(result.error || 'Failed to send email');
			}
		} catch (error) {
			console.error('Error sending email:', error);
		} finally {
			loading = false;
		}
	}

	onMount(() => {
		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 200);
		animate = true;
	});

	function handleAnchorClick(event) {
		event.preventDefault();
		const link = event.currentTarget;
		const anchorId = new URL(link.href).hash.replace('#', '');
		const anchor = document.getElementById(anchorId);
		if (anchor) {
			window.scrollTo({ top: anchor.offsetTop, behavior: 'smooth' });
		}
	}

	const reasons = [
		{
			icon: 'leaf',
			title: "Wilderness you won't find elsewhere",
			body: 'UNESCO-listed rainforests, dramatic coastlines, and mountain views all within day-trip range.'
		},
		{
			icon: 'food',
			title: 'Food that actually hits',
			body: "Fresh oysters, craft cider, and award-winning produce. Tasmania's food scene is no joke."
		},
		{
			icon: 'plane',
			title: 'Affordable direct flights',
			body: 'Direct flights from Melbourne and Sydney make Tasmania one of the most accessible getaways from VIC and NSW.'
		}
	];

	const groupPerks = [
		'Reduced per-person rate on selected tours',
		'Priority booking during peak season',
		'Flexible payment — split between the group',
		'Dedicated guide for your group on private tours',
		'Option to customise your itinerary'
	];

	const validTours = Array.isArray(allTours) ? allTours : [];

	const dayTourCodes = [
		'P18ZC5',
		'PJHLE0',
		'PNVP8M',
		'P01GQC',
		'PQ5CJC',
		'PP0SGV',
		'PQKW4C',
		'PMPLBM',
		'PTC0KJ',
		'PNNK81',
		'PCBKLS',
		'PHJFAH'
	];

	const multiDayTourCodes = [
		'PU9VRB',
		'P6GE12',
		'P1AHF0',
		'P0QKC2',
		'PHKZNS',
		'PKQPM9',
		'PTNCNK',
		'PM5WCG',
		'PBRPKL',
		'PVHGUQ',
		'P0V9R2',
		'PTWH1T'
	];

	function getOrderedTours(codes, productType) {
		return codes
			.map((code) => {
				const tour = validTours.find((t) => t.productCode === code);
				return tour ? { ...tour, productType } : null;
			})
			.filter(Boolean);
	}

	const allDayTours = validTours.filter(
		(tour) => tour.productType === 'DAYTOUR' || tour.productType === 'DAY TOUR'
	);
	const allMultiDayTours = validTours.filter(
		(tour) => tour.productType === 'MULTIDAYTOUR' || tour.productType === 'MULTI DAY TOUR'
	);

	const sortedDayTours =
		getOrderedTours(dayTourCodes, 'DAYTOUR').length > 0
			? getOrderedTours(dayTourCodes, 'DAYTOUR')
			: allDayTours;

	const sortedMultiDayTours =
		getOrderedTours(multiDayTourCodes, 'MULTIDAYTOUR').length > 0
			? getOrderedTours(multiDayTourCodes, 'MULTIDAYTOUR')
			: allMultiDayTours;

	const steps = [
		{
			number: '01',
			title: 'Pick your tours',
			body: 'Browse the lineup and choose the experiences you want — mix day tours and multi-day trips.'
		},
		{
			number: '02',
			title: 'Get your group together',
			body: '4+ people unlocks group pricing. Contact us to confirm numbers and lock in your group rate.'
		},
		{
			number: '03',
			title: 'Travel any time of year',
			body: 'Tasmania rewards every season. Mid-semester trips qualify for a 20% discount on all day tours — no need to wait for summer.'
		},
		{
			number: '04',
			title: 'Book and go',
			body: 'Secure your spots online or via phone. Split payments among the group — no stress.'
		}
	];


	const reviews = [
		{
			name: 'Carl L',
			tour: 'Port Arthur Day Tour',
			title: 'Arrived in Hobart with no plan',
			body: 'Arrived in Hobart with no plan and stepped off the ship straight onto a Tassie Tours bus. Best decision of the trip. Our guide was incredible.'
		},
		{
			name: 'Lottie S',
			tour: 'Bruny Island Day Tour',
			title: 'Bruny Island was absolutely spectacular',
			body: "Bruny Island was absolutely spectacular. Guide Jodi was amazing — so knowledgeable and fun. We'll 100% be recommending this to our friends."
		},
		{
			name: 'Marilyn P',
			tour: 'Bruny Island Day Tour',
			title: 'One of the highlights of our three-week trip',
			body: "One of the highlights of our three-week trip. The beaches are spectacular, the rainforest exquisite. We stopped for oysters — freshest I've ever had."
		}
	];

	const faqs = [
		{
			q: 'How do I access group pricing?',
			a: "Contact us directly through the enquiry form or by phone with your group size and preferred tour dates. We'll confirm your group rate and lock in your booking."
		},
		{
			q: "What's the minimum group size for a group rate?",
			a: 'Groups of 4 or more qualify for the Friends Group deal. Larger groups may be eligible for private tour arrangements — just ask.'
		},
		{
			q: 'Do you need student ID?',
			a: 'Yes — a valid student ID is required to access student pricing and group deal offers. Please have yours ready at the time of booking and on the day of your tour.'
		},
		{
			q: 'Can we split payment across the group?',
			a: "Yes — just contact us when booking and we can help coordinate split payments across your group so no one's out of pocket upfront."
		},
		{
			q: "When's the best time to visit?",
			a: "Tasmania is genuinely great year-round, and mid-semester periods (typically April–May and August–September) unlock a 20% discount on all day tours. Summer is an easy sell — long daylight hours, warm beaches, and festival season. But winter is where Tasmania surprises you. Think misty mountain mornings, crackling pub fireplaces, empty wilderness beaches that feel entirely yours, and whisky distilleries that suddenly make perfect sense. The food and wine scene doesn't slow down in winter — it gets cosier. Wildlife gets more active. And you'll have the national parks largely to yourself. Embrace the cool: pack a good jacket, book a winter trip, and discover why locals reckon it's actually the best time to visit."
		}
	];
</script>

<Seo
	title="Student Tasmania — VIC & NSW Group Tours | Tassie Tours"
	description="Student-friendly Tasmania tours with group rates, flexible booking, and guides who actually make it good. Groups of 4+ unlock the Friends Group deal."
	keywords="student Tasmania tours, student group tours Tasmania, Tasmania VIC NSW, cheap Tasmania tours students, group rates Tasmania, university holiday Tasmania"
	siteName="Tassie Tours"
	imageURL="/images/hero-carousel-1.jpg"
	logo="/src/lib/assets/logo/logo-black.png"
	author="Tassie Tours"
	type="website"
	name="Tassie Tours"
	index={true}
	twitter={true}
	openGraph={true}
	schemaOrg={true}
/>

<!-- Hero -->
<section class="relative mx-auto flex h-[600px] flex-col items-center justify-center">
	<div
		class="absolute bottom-0 left-0 z-10 h-48 w-full"
		style="clip-path: polygon(26% 96%,100% 73%,100% 100%,0 100%,0 88%);"
	>
		<div class="h-full bg-white"></div>
	</div>
	<enhanced:img
		src={heroImage}
		alt="Tasmania wilderness — student group tours"
		class="absolute inset-0 h-full w-full object-cover"
		sizes="100vw"
		fetchpriority="high"
	/>
	<div class="absolute inset-0 bg-foreground/55"></div>
	<div
		class={`z-10 mx-auto flex max-w-4xl flex-col items-center justify-center px-4 text-center ${
			animate ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
		}`}
		style="transition: all 0.9s cubic-bezier(0.17, 0.55, 0.55, 1) 0.5s"
	>
		<span
			class="mb-4 inline-block rounded-full bg-[#ffd451] px-4 py-1.5 text-sm font-semibold text-foreground"
		>
			For groups of 4 or more
		</span>
		<h1
			class="max-w-4xl font-heading text-4xl font-bold tracking-tight text-background sm:text-5xl md:text-6xl"
		>
			The Friends Group deal. Book as 4+
		</h1>
		<div class="mx-auto mt-6 flex max-w-2xl items-center">
			<p class="text-md mx-auto text-center font-semibold text-background lg:text-lg">
				Get your crew together and unlock group pricing across a range of Tassie Tours day and
				multi-day experiences. The more friends you bring, the better value it gets — and honestly,
				Tasmania's better with people you like.
			</p>
		</div>
		<p class="mt-3 max-w-2xl text-center text-sm font-semibold text-[#ffd451]">
			* A valid student ID is required to receive student rates.
		</p>
		<div class="mx-auto flex flex-col items-center justify-center gap-4 sm:flex-row">
			<Button
				data-sveltekit-noscroll
				onclick={handleAnchorClick}
				href="#bundle-save"
				class="pointer-events-auto mx-auto mt-8 rounded-full bg-tt-red px-6 font-semibold hover:bg-tt-red/90 lg:text-lg"
				>View tours</Button
			>
			<Button
				data-sveltekit-noscroll
				onclick={handleAnchorClick}
				variant="outline"
				href="#group-deal"
				class="pointer-events-auto mx-auto mt-8 rounded-full border-2 bg-transparent px-6 font-semibold text-background lg:text-lg"
				>Group rates →</Button
			>
		</div>
	</div>
</section>

<!-- Bundled Tours Highlight -->
<section
	id="bundle-save"
	class="mx-auto flex max-w-7xl flex-col overflow-hidden px-4 pb-12 pt-20 lg:overflow-visible"
>
	<p class="text-sm font-semibold uppercase tracking-widest text-[#194139]">Bundle and Save</p>
	<h2
		class="mt-10 scroll-m-20 pb-8 font-heading text-2xl font-semibold tracking-tight transition-colors first:mt-0 lg:text-3xl"
	>
		The best of Tassie, packaged up for you
	</h2>
	<div class="mt-4 w-full">
		<Carousel.Root class="w-full" opts={{ align: 'start' }}>
			<Carousel.Content peek>
				{#each sortedMultiDayTours.slice(0, 9) as tour}
					<Carousel.Item class="basis-[85%] lg:basis-1/3">
						<TourCard {tour} />
					</Carousel.Item>
				{/each}
			</Carousel.Content>
			<Carousel.Previous class="hidden lg:flex" />
			<Carousel.Next class="hidden lg:flex" />
		</Carousel.Root>
	</div>
	<div class="mt-6 flex justify-center pb-8">
		<a
			href="/tours#bundledDayTours"
			data-sveltekit-noscroll
			class="flex items-center gap-2 rounded-full bg-muted px-6 py-3 text-sm font-semibold text-foreground hover:bg-muted/70"
		>
			See the full range of bundled day tours <ArrowRight class="h-4 w-4" />
		</a>
	</div>
</section>

<!-- How to Book -->
<section class="bg-white py-24">
	<div class="mx-auto max-w-7xl px-4">
		<div class="mx-auto max-w-5xl">
			<p class="text-sm font-semibold uppercase tracking-widest text-[#194139]">How to book</p>
			<h2 class="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
				Four steps and you're good to go
			</h2>
		</div>
		<div class="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
			{#each steps as step}
				<div class="flex flex-col gap-3">
					<span class="font-heading text-4xl font-bold text-[#194139]/20">{step.number}</span>
					<h3 class="font-heading text-lg font-semibold">{step.title}</h3>
					<p class="text-sm leading-6 text-gray-600">{step.body}</p>
				</div>
			{/each}
		</div>
	</div>
</section>

<!-- Social Proof -->
<section class="mx-auto flex max-w-7xl flex-col px-4 pb-20 pt-20">
	<h2
		class="scroll-m-20 pb-2 font-heading text-2xl font-semibold tracking-tight transition-colors first:mt-0 lg:text-3xl"
	>
		What past travellers say
	</h2>
	<p class="text-sm text-gray-500">2,000+ five-star reviews across TripAdvisor and Google</p>
	<div class="mx-auto mt-6 w-full max-w-7xl pb-12 lg:mt-12">
		<div class="group col-span-1 flex w-full flex-col">
			<div
				class="relative flex overflow-hidden rounded-2xl bg-gray-900 px-6 pb-32 pt-12 shadow-xl sm:px-10 md:px-12 lg:h-[350px] lg:items-center lg:rounded-3xl lg:px-20 lg:py-20"
			>
				<enhanced:img
					class="absolute inset-0 h-full w-full object-cover"
					src={cradleImage}
					alt="2,000+ five-star reviews"
					loading="lazy"
				/>
				<div class="absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all"></div>
				<div class="relative mx-auto max-w-xl lg:mx-0">
					<h3
						class="text-center font-heading text-2xl font-bold text-background lg:text-left lg:text-4xl"
					>
						2,000+ five-star reviews and counting
					</h3>
				</div>
			</div>
		</div>
		<div class="mx-auto grid-cols-4 lg:grid">
			<div class="col-span-1 hidden flex-col pt-4 lg:flex">
				<TripadvisorAward />
				<enhanced:img
					src="/src/lib/assets/images/tassie-specialist.png"
					alt="Tassie Specialist Award Winner"
					class="ml-8 mt-4 w-36"
					loading="lazy"
				/>
			</div>
			<div class="col-span-3 -mt-24 px-8 lg:px-0 lg:pr-12">
				<Carousel.Root class="w-full" opts={{ align: 'start' }}>
					<Carousel.Content peek>
						{#each reviews as review}
							<Carousel.Item class="basis-[85%] lg:basis-1/3">
								<div class="w-full text-left">
									<Card.Root class="w-full overflow-hidden rounded-2xl border-none shadow-none bg-muted">
										<Card.Header class="relative w-full">
											<div class="flex gap-0">
												<Star class="fill-tt-red text-tt-red" />
												<Star class="fill-tt-red text-tt-red" />
												<Star class="fill-tt-red text-tt-red" />
												<Star class="fill-tt-red text-tt-red" />
												<Star class="fill-tt-red text-tt-red" />
											</div>
											<Card.Title class="h-14 line-clamp-2 font-heading text-lg leading-snug"
												>{review.title}</Card.Title
											>
											<Card.Description>{review.name} — {review.tour}</Card.Description>
										</Card.Header>
										<Card.Content class="grid gap-4">
											<div class="relative">
												<p class="h-48 text-sm leading-6">{review.body}</p>
												<div
													class="absolute -bottom-6 h-12 w-full bg-gradient-to-t from-muted to-muted/40"
												></div>
											</div>
										</Card.Content>
									</Card.Root>
								</div>
							</Carousel.Item>
						{/each}
					</Carousel.Content>
					<Carousel.Previous class="hidden lg:flex" />
					<Carousel.Next class="hidden lg:flex" />
				</Carousel.Root>
			</div>
		</div>
	</div>
</section>

<!-- Friends Group Deal -->
<section class="relative bg-[#194139] pb-32 pt-24" id="group-deal">
	<div
		class="absolute -top-48 left-0 z-10 h-48 w-full"
		style="clip-path: polygon(27% 94%,66% 86%,100% 91%,100% 100%,0 100%,0 88%);"
	>
		<div class="h-full bg-[#194139]"></div>
	</div>
	<div
		class="absolute bottom-0 left-0 z-10 h-48 w-full"
		style="clip-path: polygon(27% 94%,66% 86%,100% 91%,100% 100%,0 100%,0 88%);"
	>
		<div class="h-full bg-white"></div>
	</div>
	<div class="mx-auto max-w-7xl px-4">
		<div class="mx-auto max-w-3xl text-center">
			<span
				class="inline-block rounded-full bg-[#ffd451] px-4 py-1.5 text-sm font-semibold text-foreground"
			>
				Day Tours From Just $99
			</span>
			<h2 class="mt-6 font-heading text-3xl font-semibold tracking-tight text-white sm:text-4xl">
				Your group. Our island. Your adventure.
			</h2>
			<p class="mx-auto mt-6 max-w-2xl leading-7 text-white/80">
				Real Tasmanian experiences, without the real Tasmanian price tag. We've built
				student-friendly tours with group rates, flexible booking, and guides who actually make it
				good.
			</p>
			<Button
				data-sveltekit-noscroll
				onclick={handleAnchorClick}
				href="#enquiry"
				class="pointer-events-auto mt-8 rounded-full bg-tt-red px-8 font-semibold hover:bg-tt-red/90 lg:text-lg"
			>
				Enquire about group rates
			</Button>
		</div>
		<div class="mx-auto mt-12 max-w-2xl">
			<ul class="mx-auto w-fit space-y-4">
				{#each groupPerks as perk}
					<li class="flex items-start gap-3">
						<div
							class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#ffd451]"
						>
							<Check class="h-3.5 w-3.5 text-foreground" />
						</div>
						<span class="text-white/90">{perk}</span>
					</li>
				{/each}
			</ul>
		</div>
	</div>
</section>

<!-- Tour Listings -->
<section
	class="mx-auto flex max-w-7xl flex-col overflow-hidden px-4 pb-12 pt-20 lg:overflow-visible"
>
	<p class="text-sm font-semibold uppercase tracking-widest text-[#194139]">
		Popular tours for students
	</p>
	<h2
		class="mt-10 scroll-m-20 pb-8 font-heading text-2xl font-semibold tracking-tight transition-colors first:mt-0 lg:text-3xl"
	>
		Great for groups, from quick day trips to multi-day adventures
	</h2>
	<Tabs.Root value="dayTours">
		<Tabs.List class="h-full rounded-full px-2 py-1.5">
			<Tabs.Trigger
				class="rounded-full font-semibold data-[state=active]:bg-tt-red data-[state=active]:text-background"
				value="dayTours">Day tours</Tabs.Trigger
			>
			<Tabs.Trigger
				class="rounded-full font-semibold data-[state=active]:bg-tt-red data-[state=active]:text-background"
				value="multiDayTours">Multi-day tours</Tabs.Trigger
			>
		</Tabs.List>
		<Tabs.Content value="dayTours">
			<div class="mt-4 w-full">
				<Carousel.Root class="w-full" opts={{ align: 'start' }}>
					<Carousel.Content peek>
						{#each sortedDayTours as tour}
							<Carousel.Item class="basis-[85%] lg:basis-1/3">
								<TourCard {tour} />
							</Carousel.Item>
						{/each}
					</Carousel.Content>
					<Carousel.Previous class="hidden lg:flex" />
					<Carousel.Next class="hidden lg:flex" />
				</Carousel.Root>
			</div>
			<a
				href="/tours#dayTours"
				data-sveltekit-noscroll
				class="mt-6 flex items-center gap-2 text-sm font-semibold text-[#194139] hover:underline"
			>
				View all day tours <ArrowRight class="h-4 w-4" />
			</a>
		</Tabs.Content>
		<Tabs.Content value="multiDayTours">
			<div class="mt-4 w-full">
				<Carousel.Root class="w-full" opts={{ align: 'start' }}>
					<Carousel.Content peek>
						{#each sortedMultiDayTours as tour}
							<Carousel.Item class="basis-[85%] lg:basis-1/3">
								<TourCard {tour} />
							</Carousel.Item>
						{/each}
					</Carousel.Content>
					<Carousel.Previous class="hidden lg:flex" />
					<Carousel.Next class="hidden lg:flex" />
				</Carousel.Root>
			</div>
			<a
				href="/tours#multiDayTours"
				data-sveltekit-noscroll
				class="mt-6 flex items-center gap-2 text-sm font-semibold text-[#194139] hover:underline"
			>
				View all multi-day tours <ArrowRight class="h-4 w-4" />
			</a>
		</Tabs.Content>
	</Tabs.Root>
</section>

<!-- Why Tassie Is the Trip -->
<div class="bg-white pt-12">
	<div class="mx-auto max-w-7xl px-4">
		<div class="mx-auto max-w-5xl">
			<p class="text-sm font-semibold uppercase tracking-widest text-[#194139]">
				Why Tassie Is the Trip
			</p>
			<h2 class="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
				More than a weekend away, it's the trip you'll actually talk about
			</h2>
		</div>

		<div class="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-8 pb-20 sm:grid-cols-3">
			{#each reasons as reason}
				<div class="flex flex-col gap-3 rounded-2xl bg-secondary p-6">
					<div class="flex h-12 w-12 items-center justify-center rounded-full bg-[#194139]/10">
						{#if reason.icon === 'leaf'}
							<Leaf class="h-6 w-6 text-[#194139]" />
						{:else if reason.icon === 'food'}
							<Utensils class="h-6 w-6 text-[#194139]" />
						{:else if reason.icon === 'plane'}
							<Plane class="h-6 w-6 text-[#194139]" />
						{/if}
					</div>
					<h3 class="font-heading text-lg font-semibold">{reason.title}</h3>
					<p class="text-sm leading-6 text-gray-600">{reason.body}</p>
				</div>
			{/each}
		</div>
	</div>
</div>

<!-- FAQ -->
<section class="mx-auto max-w-3xl px-4 py-24">
	<p class="text-sm font-semibold uppercase tracking-widest text-[#194139]">FAQ</p>
	<h2 class="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
		Common questions
	</h2>
	<div class="mt-10">
		<Accordion.Root type="single" collapsible class="w-full">
			{#each faqs as faq, i}
				<Accordion.Item value={`faq-${i}`}>
					<Accordion.Trigger class="text-left font-heading text-base font-semibold">
						{faq.q}
					</Accordion.Trigger>
					<Accordion.Content class="text-sm leading-7 text-gray-600">
						{faq.a}
					</Accordion.Content>
				</Accordion.Item>
			{/each}
		</Accordion.Root>
	</div>
</section>

<!-- Enquiry Form -->
<section class="mx-auto px-4 pb-12 pt-12" id="enquiry">
	<div class="mx-auto max-w-7xl">
		<h2
			class="text-center font-heading text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl"
		>
			Claim your group deal
		</h2>
		<p class="mx-auto mt-4 max-w-2xl text-center leading-7 text-gray-600">
			Fill in your details and one of our team will get back to you with your group's pricing and
			availability. Groups of 4+ unlock our Friends Group rates — no hidden costs, just great tours.
		</p>
		<div class="mx-auto mt-6 flex max-w-3xl">
			<form
				onsubmit={handleSubmit}
				data-sveltekit-preload-data="off"
				class="relative flex w-full flex-col items-center justify-center gap-10 px-4 pb-12 pt-6"
			>
				<div class="flex w-full flex-col gap-4 sm:flex-row">
					<div class="flex w-full flex-col gap-1.5 sm:w-1/2">
						<Label for="firstName">First name</Label>
						<Input required type="text" id="firstName" placeholder="First name" bind:value={firstName} />
					</div>
					<div class="flex w-full flex-col gap-1.5 sm:w-1/2">
						<Label for="lastName">Last name</Label>
						<Input required type="text" id="lastName" placeholder="Last name" bind:value={lastName} />
					</div>
				</div>

				<div class="flex w-full flex-col gap-1.5">
					<Label for="email">Email address</Label>
					<Input required type="email" id="email" placeholder="email@uni.edu.au" bind:value={email} />
				</div>

				<div class="flex w-full flex-col gap-1.5">
					<Label for="university">University or institution</Label>
					<Input type="text" id="university" placeholder="e.g. University of Melbourne" bind:value={university} />
				</div>

				<div class="flex w-full flex-col gap-1.5">
					<Label>Group size</Label>
					<Select.Root type="single" bind:value={groupSize}>
						<Select.Trigger>{groupSizeTrigger}</Select.Trigger>
						<Select.Content>
							<Select.Group>
								{#each groupSizeOptions as option}
									<Select.Item value={option.value} label={option.label}>{option.label}</Select.Item>
								{/each}
							</Select.Group>
						</Select.Content>
					</Select.Root>
				</div>

				<div class="flex w-full flex-col gap-1.5">
					<Label for="message">Which tours interest you? Any preferred dates?</Label>
					<Textarea
						id="message"
						placeholder="e.g. Bruny Island day tour, Cradle Mountain — looking at late July or August..."
						class="min-h-28"
						bind:value={message}
					/>
				</div>

				<div class="relative mt-6 flex h-32 w-full items-center justify-center">
					{#if !submitted}
						<div
							class="absolute inset-0 flex justify-center"
							in:fly={{ x: -50, duration: 300 }}
							out:fly={{ x: -50, duration: 300 }}
						>
							{#if !loading}
								<Button
									type="submit"
									class="z-50 min-w-32 overflow-hidden rounded-full bg-tt-red p-3 text-center font-semibold text-background transition-all hover:bg-tt-red/80"
								>
									Send enquiry →
								</Button>
							{:else}
								<Button
									variant="outline"
									disabled
									class="z-50 flex min-w-32 overflow-hidden rounded-full bg-tt-red p-3 text-center font-semibold text-background transition-all"
								>
									<Loader2 class="mr-2 h-4 w-4 animate-spin" />
								</Button>
							{/if}
						</div>
					{:else}
						<div
							class="absolute inset-0 mx-auto flex w-full max-w-[500px] items-center justify-center"
							in:fly={{ x: 50, duration: 300, delay: 300 }}
							out:fly={{ x: -50, duration: 300 }}
						>
							<Alert.Root onclick={() => (submitted = false)} class="text-left hover:bg-accent">
								<CheckCircle class="h-4 w-4" />
								<Alert.Title>Enquiry received!</Alert.Title>
								<Alert.Description class="text-wrap">
									One of our team will be in touch within 3 business days to confirm your group's
									pricing and availability.
								</Alert.Description>
							</Alert.Root>
						</div>
					{/if}
				</div>
			</form>
		</div>
	</div>
</section>

